import { Fragment, useMemo, useState } from 'react';
import { Badge, Button, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import { cx } from '@/lib/cx';
import {
  PiCardField,
  PiCardList,
  PiEmpty,
  PiExpandButton,
  PiLoading,
  PiSection,
  PiSortHeader,
  piCount,
} from '../pi-kit';
import type {
  ChallengeClientSchoolView,
  ChallengeEditionView,
  ChallengesView,
} from './challenges-model';

/**
 * Challenge Hub by client school: one row per partner school, the editions it
 * partnered, and how many challenge participants LATER applied to / were
 * admitted by / enrolled at that school.
 *
 * - The school links to its SEED EMERGE company when there is one.
 * - "Partner of" resolves edition keys to their titles from the same response;
 *   an unknown key shows as the key. The full list expands inline per row.
 * - Default order: applied, then admitted, then name. A missing figure always
 *   sorts last, whichever direction.
 */

type SortKey = 'school' | 'editions' | 'applied' | 'admitted' | 'enrolled';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 50;
const COLUMN_COUNT = 6;

function nameOf(row: ChallengeClientSchoolView): string {
  return row.company?.name ?? row.schoolName;
}

function compareName(a: ChallengeClientSchoolView, b: ChallengeClientSchoolView): number {
  return nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: 'base' });
}

/** Nulls last regardless of direction. */
function compareCounts(a: number | null, b: number | null, dir: SortDir): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return dir === 'desc' ? b - a : a - b;
}

function compareRows(
  a: ChallengeClientSchoolView,
  b: ChallengeClientSchoolView,
  key: SortKey,
  dir: SortDir,
): number {
  switch (key) {
    case 'school': {
      const byName = compareName(a, b);
      return dir === 'asc' ? byName : -byName;
    }
    case 'editions':
      return (
        compareCounts(a.partnerOf.length, b.partnerOf.length, dir) ||
        compareCounts(a.applied, b.applied, 'desc') ||
        compareName(a, b)
      );
    case 'applied':
      return (
        compareCounts(a.applied, b.applied, dir) ||
        compareCounts(a.admitted, b.admitted, 'desc') ||
        compareName(a, b)
      );
    case 'admitted':
      return (
        compareCounts(a.admitted, b.admitted, dir) ||
        compareCounts(a.applied, b.applied, 'desc') ||
        compareName(a, b)
      );
    case 'enrolled':
      return (
        compareCounts(a.enrolled, b.enrolled, dir) ||
        compareCounts(a.applied, b.applied, 'desc') ||
        compareName(a, b)
      );
  }
}

function SchoolCell({ row }: { row: ChallengeClientSchoolView }) {
  return (
    <div className="min-w-0">
      {row.company !== null ? (
        <TextLink to={`/companies/${row.company.id}`} className="block truncate font-medium">
          {row.company.name}
        </TextLink>
      ) : (
        <div className="truncate font-medium text-fg">{row.schoolName}</div>
      )}
      {row.universityName !== null && row.universityName !== nameOf(row) && (
        <div className="truncate text-xs text-fg-muted">{row.universityName}</div>
      )}
    </div>
  );
}

function PartnerBadges({
  keys,
  editions,
}: {
  keys: string[];
  editions: Map<string, ChallengeEditionView>;
}) {
  if (keys.length === 0) return <span className="text-fg-muted">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {keys.map((key) => (
        <Badge key={key} tone="neutral" variant="outline" wrap>
          {editions.get(key)?.title ?? key}
        </Badge>
      ))}
    </div>
  );
}

function PartnerDetail({
  keys,
  editions,
}: {
  keys: string[];
  editions: Map<string, ChallengeEditionView>;
}) {
  if (keys.length === 0) return <span className="text-sm text-fg-muted">—</span>;
  return (
    <ul className="grid gap-1 text-sm">
      {keys.map((key) => {
        const edition = editions.get(key);
        return (
          <li key={key} className="flex min-w-0 items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-fg">{edition?.title ?? key}</span>
            <span className="shrink-0 tabular-nums text-fg-muted">
              {edition === undefined ? '—' : `${piCount(edition.participants)} participants`}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function ChallengeSchoolsTable({
  data,
  loading,
}: {
  data: ChallengesView | null;
  loading: boolean;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('applied');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const editions = useMemo(
    () => new Map((data?.editions ?? []).map((edition) => [edition.key, edition])),
    [data],
  );

  const rows = useMemo<ChallengeClientSchoolView[]>(() => {
    if (data === null) return [];
    return [...data.clientSchools].sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [data, sortKey, sortDir]);

  const visible = rows.slice(0, limit);
  const remaining = rows.length - visible.length;

  function sortBy(key: SortKey) {
    if (key === sortKey) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'school' ? 'asc' : 'desc');
    }
  }

  function toggle(key: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function header(key: SortKey, label: string, align: 'left' | 'right' = 'right') {
    return (
      <PiSortHeader
        label={label}
        active={sortKey === key}
        direction={sortKey === key ? sortDir : key === 'school' ? 'asc' : 'desc'}
        onClick={() => sortBy(key)}
        align={align}
      />
    );
  }

  function ariaSort(key: SortKey): 'ascending' | 'descending' | undefined {
    if (sortKey !== key) return undefined;
    return sortDir === 'asc' ? 'ascending' : 'descending';
  }

  let body;
  if (data === null) {
    body = <PiLoading variant="table" rows={5} />;
  } else if (rows.length === 0) {
    body = <PiEmpty title="No client schools for these editions." />;
  } else {
    body = (
      <div className={cx('min-w-0', loading && 'opacity-70')} aria-busy={loading || undefined}>
        {/* Desktop */}
        <div className="max-md:hidden">
          <TableWrap frozenHeader>
            <Table>
              <THead>
                <TR>
                  <TH className="w-10">
                    <span className="sr-only">Details</span>
                  </TH>
                  <TH className="min-w-64" aria-sort={ariaSort('school')}>
                    {header('school', 'School', 'left')}
                  </TH>
                  <TH numeric className="min-w-24" aria-sort={ariaSort('editions')}>
                    {header('editions', 'Editions')}
                  </TH>
                  <TH numeric className="min-w-24" aria-sort={ariaSort('applied')}>
                    {header('applied', 'Applied')}
                  </TH>
                  <TH numeric className="min-w-24" aria-sort={ariaSort('admitted')}>
                    {header('admitted', 'Admitted')}
                  </TH>
                  <TH numeric className="min-w-24" aria-sort={ariaSort('enrolled')}>
                    {header('enrolled', 'Enrolled')}
                  </TH>
                </TR>
              </THead>
              <TBody>
                {visible.map((row) => {
                  const isOpen = expanded.has(row.key);
                  return (
                    <Fragment key={row.key}>
                      <TR>
                        <TD className="w-10 pr-0">
                          {row.partnerOf.length > 0 && (
                            <PiExpandButton
                              expanded={isOpen}
                              onClick={() => toggle(row.key)}
                              label={`${isOpen ? 'Hide' : 'Show'} editions for ${nameOf(row)}`}
                            />
                          )}
                        </TD>
                        <TD className="max-w-96">
                          <SchoolCell row={row} />
                        </TD>
                        <TD numeric className="tabular-nums">
                          {piCount(row.partnerOf.length)}
                        </TD>
                        <TD numeric className="font-medium tabular-nums">
                          {piCount(row.applied)}
                        </TD>
                        <TD numeric className="tabular-nums">
                          {piCount(row.admitted)}
                        </TD>
                        <TD numeric className="tabular-nums">
                          {piCount(row.enrolled)}
                        </TD>
                      </TR>
                      {isOpen && (
                        <TR className="bg-surface-sunken">
                          <TD />
                          <TD colSpan={COLUMN_COUNT - 1}>
                            <PartnerDetail keys={row.partnerOf} editions={editions} />
                          </TD>
                        </TR>
                      )}
                    </Fragment>
                  );
                })}
              </TBody>
            </Table>
          </TableWrap>
        </div>

        {/* Phone */}
        <div className="p-3 md:hidden">
          <PiCardList
            items={visible}
            getKey={(row) => row.key}
            render={(row) => (
              <div className="grid min-w-0 gap-2">
                <SchoolCell row={row} />
                <PartnerBadges keys={row.partnerOf} editions={editions} />
                <div className="grid grid-cols-3 gap-2">
                  <PiCardField label="Applied" value={piCount(row.applied)} />
                  <PiCardField label="Admitted" value={piCount(row.admitted)} />
                  <PiCardField label="Enrolled" value={piCount(row.enrolled)} />
                </div>
              </div>
            )}
          />
        </div>

        {remaining > 0 && (
          <div className="flex justify-center border-t border-border p-2">
            <Button
              variant="ghost"
              className="h-9 max-sm:h-11"
              onClick={() => setLimit((current) => current + PAGE_SIZE)}
            >
              Show {piCount(Math.min(remaining, PAGE_SIZE))} more
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <PiSection
      title="By client school"
      count={data === null ? null : rows.length}
      flush
      id="challenges-schools"
    >
      {body}
    </PiSection>
  );
}
